import { CommandDescriptor, CommandOption, ParsedCommand } from '../../types/commandTypes'

/**
 * The alias that marks a command's options as union semantics: a command
 * advertising it (e.g. `/org` with alias `select`) renders its option popup
 * as a checkbox multi-select panel. The panel itself is generic and works
 * off the command's `options`, not a hardcoded command name.
 */
export const MULTI_SELECT_ALIAS = 'select'

/**
 * Decide whether the option popup for `descriptor` should render as a
 * multi-select panel.
 *
 * The signal is the descriptor advertising the multi-select alias — args
 * union on the backend, so every entry path (`/org`, `/select`, `/sel` +
 * click, picking from the list) gets the same checkbox panel. Commands
 * without the alias keep single-select rows.
 */
export function isMultiSelectCommand(descriptor: CommandDescriptor): boolean {
  return descriptor.aliases.some(alias => alias.toLowerCase() === MULTI_SELECT_ALIAS)
}

/**
 * Toggle `optionName` within an ordered selection list. Adding appends to the
 * end (preserving pick order for the resulting union); removing drops it while
 * keeping the relative order of the rest.
 */
export function toggleSelection(selected: string[], optionName: string): string[] {
  if (selected.includes(optionName)) {
    return selected.filter(name => name !== optionName)
  }
  return [...selected, optionName]
}

/**
 * Human-friendly, comma-joined argument labels for a command: maps each arg to
 * its option `name` via the registry (the access_scope token "DKB" -> "KBC"),
 * falling back to the raw value when the command/option isn't found.
 */
export function describeCommandArgs(command: ParsedCommand, descriptors: CommandDescriptor[]): string {
  const descriptor = descriptors.find(entry => entry.name === command.name)
  return command.args
    .map(arg => descriptor?.options.find(option => option.value === arg)?.name ?? arg)
    .join(', ')
}

/**
 * Human-friendly chip label for an applied command (`org` -> "org: KBC, SLOB").
 * Argument-less commands render as just their name.
 */
export function describeCommand(command: ParsedCommand, descriptors: CommandDescriptor[]): string {
  if (command.args.length === 0) return command.name
  return `${command.name}: ${describeCommandArgs(command, descriptors)}`
}

/**
 * Normalize for matching: lowercase and drop everything that is not a letter
 * or digit, so a typed "dvg" matches the value "DVG" and "devriesgroep"
 * matches the name "De Vries Groep" despite spacing and punctuation.
 */
function normalizeForMatch(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * Whether `option` matches the typed `filter`, comparing the normalized
 * filter against the option's normalized display name and value. An empty
 * (or punctuation-only) filter matches everything.
 */
export function optionMatchesFilter(option: CommandOption, filter: string): boolean {
  const query = normalizeForMatch(filter)
  if (!query) {
    return true
  }
  return (
    normalizeForMatch(option.name).includes(query) ||
    normalizeForMatch(option.value).includes(query)
  )
}

/**
 * Display order for the multi-select panel: the selected options pinned on
 * top in pick order — always visible, exempt from the filter — followed by
 * the unselected options matching the filter, in their original order.
 * Selected values without a matching option are skipped.
 */
export function orderMultiSelectOptions(
  options: CommandOption[],
  selected: string[],
  filter: string,
): CommandOption[] {
  const byValue = new Map(options.map(option => [option.value, option]))
  const pinned = selected
    .map(value => byValue.get(value))
    .filter((option): option is CommandOption => option !== undefined)
  const rest = options.filter(
    option => !selected.includes(option.value) && optionMatchesFilter(option, filter),
  )
  return [...pinned, ...rest]
}

/**
 * Build the single union command from a multi-select. Returns `null` when
 * nothing is selected (so callers apply no command). The selected option
 * values become the command's `args` in pick order; the backend unions
 * multiple args.
 */
export function buildSelectionCommand(
  commandName: string,
  selected: string[],
): ParsedCommand | null {
  if (selected.length === 0) {
    return null
  }
  return { name: commandName, args: [...selected] }
}
